import React from 'react';
import i18n from '../../utilities/i18n';
import {
  Button,
  LoadingSpinner,
  Table,
  VStack,
} from '@adc/parallax-component-library';
import MessageBanner from '../messageBanner/MessageBanner';
import { CreateOrganizationModal } from '../modal/CreateOrganizationModal';
import { Organization } from '../../models';
import DynamicTable, { ColumnConfig } from '../table/DynamicTable';
import DateCellRenderer from '../table/DateCellRenderer';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createOrganizationMutateOptions,
  organizationsQueryOptions,
} from '../../queryOptions';

/**
 * Organizations component that displays the details of all organizations.
 * @returns {React.JSX.Element}
 */
export default function Organizations() {
  const queryClient = useQueryClient();
  const organizations = useQuery(organizationsQueryOptions());

  const createOrganization = useMutation(
    createOrganizationMutateOptions({
      onSuccess: (organization) => {
        queryClient.setQueryData(
          organizationsQueryOptions().queryKey,
          (organizations: Organization[] | undefined) => [
            ...(organizations ?? []),
            organization,
          ],
        );
      },
    }),
  );

  const organizationsTableColumnData: ColumnConfig[] = [
    { label: i18n.t('Entity.name'), dataKey: 'name' },
    { label: i18n.t('Entity.type'), dataKey: 'type' },
    { label: i18n.t('Entity.region'), dataKey: 'region' },
    { label: i18n.t('Entity.description'), dataKey: 'description' },
    { label: i18n.t('Entity.createdBy'), dataKey: 'createdBy' },
    { label: i18n.t('Entity.createdOn'), dataKey: 'createdOn' },
  ];
  const organizationsTableCellRenderer = (
    dataKey: string,
    organization: Organization,
  ) => {
    if (dataKey === 'createdOn') {
      return <DateCellRenderer value={organization.createdOn} key={dataKey} />;
    } else {
      return (
        <Table.TextCell key={dataKey}>{organization[dataKey]}</Table.TextCell>
      );
    }
  };

  return (
    <VStack height={'100%'}>
      {organizations.isPending ? (
        <VStack alignItems="center" justifyContent="center" flexGrow={1}>
          <LoadingSpinner testID="organizations-list-loading-spinner" />
        </VStack>
      ) : null}

      {organizations.isError ? (
        <MessageBanner
          type="danger"
          headerText={i18n.t('Page_Organizations.error_title')}
          bodyText={i18n.t('Page_Organizations.error_default')}
        />
      ) : null}

      {organizations.isSuccess ? (
        <DynamicTable
          isLoading={organizations.isPending}
          hasLoadingError={organizations.isError}
          title={i18n.t('Page_Organizations.title')}
          description={i18n.t('Page_Organizations.table')}
          columnData={organizationsTableColumnData}
          rowData={organizations.data || []}
          cellRenderer={organizationsTableCellRenderer}
        >
          <CreateOrganizationModal
            trigger={
              <Button
                size="medium"
                label={i18n.t('Page_Organizations.createOrganization')}
                onPress={() => {}}
                disabled={createOrganization.isPending}
                aria-label={i18n.t('Page_Organizations.createOrganization')}
              />
            }
            onSuccess={async (newOrganization: Organization) => {
              await createOrganization.mutateAsync({
                organization: newOrganization,
              });
            }}
          />
        </DynamicTable>
      ) : null}
    </VStack>
  );
}
